import React, { useState, useEffect, useRef } from "react";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../contexts/AuthContext";
import { API_URL } from "../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { formatCurrency } from "../../lib/utils";
import {
    TrendingUp, Users, FolderKanban, Clock, ArrowRightLeft, Loader2,
    FileDown, FileSpreadsheet, Mail, Sparkles,
} from "lucide-react";

const CHART_COLORS = ["#2563EB", "#10B981", "#F97316", "#8B5CF6", "#EF4444", "#06B6D4", "#EC4899", "#F59E0B"];

const ChartTooltipStyle = {
    backgroundColor: "hsl(var(--card))",
    border: "1px solid hsl(var(--border))",
    borderRadius: "8px",
    fontSize: "12px",
};

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
    <div className="flex items-center gap-3 p-4 rounded-xl bg-secondary/60">
        <div className={`p-2.5 rounded-lg ${color}`}>
            <Icon className="w-4 h-4 text-white" />
        </div>
        <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="font-heading font-bold text-xl">{value}</p>
            {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
        </div>
    </div>
);

export const AnalyticsSection = () => {
    const { get } = useApi();
    const { token, user } = useAuth();
    const [productivity, setProductivity] = useState([]);
    const [teamPerf, setTeamPerf] = useState([]);
    const [monthlyRev, setMonthlyRev] = useState([]);
    const [quoteConv, setQuoteConv] = useState(null);
    const [timeDist, setTimeDist] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exportingPdf, setExportingPdf] = useState(false);
    const [exportingExcel, setExportingExcel] = useState(false);
    const [sendingReport, setSendingReport] = useState(false);
    const [reportPreview, setReportPreview] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const analyticsRef = useRef(null);
    const [aiRecommendations, setAiRecommendations] = useState([]);
    const [loadingAi, setLoadingAi] = useState(false);

    useEffect(() => {
        const fetchAll = async () => {
            const results = await Promise.allSettled([
                get("/dashboard/analytics/productivity"),
                get("/dashboard/analytics/team-performance"),
                get("/dashboard/analytics/monthly-revenue"),
                get("/dashboard/analytics/quote-conversion"),
                get("/dashboard/analytics/time-distribution"),
            ]);
            if (results[0].status === "fulfilled") setProductivity(results[0].value);
            if (results[1].status === "fulfilled") setTeamPerf(results[1].value);
            if (results[2].status === "fulfilled") setMonthlyRev(results[2].value);
            if (results[3].status === "fulfilled") setQuoteConv(results[3].value);
            if (results[4].status === "fulfilled") setTimeDist(results[4].value);
            setLoading(false);
        };
        fetchAll();
    }, [get]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-48">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
        );
    }


    const handleSendWeeklyReport = async () => {
        setSendingReport(true);
        try {
            const res = await fetch(`${API_URL}/reports/send-weekly`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();
            if (data.status === "sent") {
                const { toast: sonnerToast } = await import("sonner");
                sonnerToast.success(`Rapport envoye a ${data.sent_to.join(", ")}`);
            } else {
                const { toast: sonnerToast } = await import("sonner");
                sonnerToast.info("Rapport genere (envoi email desactive en preview)");
            }
        } catch {
            const { toast: sonnerToast } = await import("sonner");
            sonnerToast.error("Erreur lors de l'envoi du rapport");
        } finally {
            setSendingReport(false);
        }
    };

    const handlePreviewReport = async () => {
        try {
            const res = await fetch(`${API_URL}/reports/weekly-preview`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();
            setReportPreview(data);
            setShowPreview(true);
        } catch {
            const { toast: sonnerToast } = await import("sonner");
            sonnerToast.error("Erreur lors de la generation du rapport");
        }
    };

    const handleAiSummary = async () => {
        setLoadingAi(true);
        try {
            const res = await fetch(`${API_URL}/reports/weekly-ai-summary`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();
            setAiRecommendations(data.recommendations || []);
            if (!data.recommendations?.length) {
                const { toast: sonnerToast } = await import("sonner");
                sonnerToast.info("Aucune recommandation generee");
            }
        } catch {
            const { toast: sonnerToast } = await import("sonner");
            sonnerToast.error("Erreur lors de la generation du resume IA");
        } finally {
            setLoadingAi(false);
        }
    };

    const handleExportExcel = async () => {
        setExportingExcel(true);
        try {
            const res = await fetch(`${API_URL}/dashboard/export/excel`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Export failed");
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `orkestria_rapport_${new Date().toISOString().slice(0, 10)}.xlsx`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch {
            console.error("Excel export error");
        } finally {
            setExportingExcel(false);
        }
    };

    const handleExportPdf = async () => {
        setExportingPdf(true);
        try {
            const html2canvas = (await import("html2canvas")).default;
            const jsPDF = (await import("jspdf")).default;
            const el = analyticsRef.current;
            if (!el) return;
            const canvas = await html2canvas(el, { scale: 1.5, useCORS: true, backgroundColor: "#ffffff" });
            const imgData = canvas.toDataURL("image/jpeg", 0.8);
            const pdf = new jsPDF("p", "mm", "a4");
            const pageW = pdf.internal.pageSize.getWidth();
            const pageH = pdf.internal.pageSize.getHeight();
            const imgW = pageW - 20;
            const imgH = (canvas.height * imgW) / canvas.width;
            
            pdf.setFontSize(16);
            pdf.setFont("helvetica", "bold");
            pdf.text("Orkestria - Rapport Analytique", 10, 15);
            pdf.setFontSize(9);
            pdf.setFont("helvetica", "normal");
            pdf.text(`Genere le ${new Date().toLocaleDateString("fr-FR")}`, 10, 22);
            
            let yPos = 28;
            let remainingH = imgH;
            let srcY = 0;
            
            while (remainingH > 0) {
                const sliceH = Math.min(remainingH, pageH - yPos - 10);
                const sliceCanvasH = (sliceH / imgH) * canvas.height;
                
                const sliceCanvas = document.createElement("canvas");
                sliceCanvas.width = canvas.width;
                sliceCanvas.height = sliceCanvasH;
                const ctx = sliceCanvas.getContext("2d");
                ctx.drawImage(canvas, 0, srcY, canvas.width, sliceCanvasH, 0, 0, canvas.width, sliceCanvasH);
                
                pdf.addImage(sliceCanvas.toDataURL("image/jpeg", 0.8), "JPEG", 10, yPos, imgW, sliceH);
                
                remainingH -= sliceH;
                srcY += sliceCanvasH;
                
                if (remainingH > 0) {
                    pdf.addPage();
                    yPos = 10;
                }
            }
            
            pdf.save(`orkestria_rapport_${new Date().toISOString().slice(0, 10)}.pdf`);
        } catch (e) {
            console.error("PDF export error:", e);
        } finally {
            setExportingPdf(false);
        }
    };

    const totalHours = timeDist.reduce((sum, d) => sum + d.hours, 0);

    return (
        <div className="space-y-6" data-testid="analytics-section" ref={analyticsRef}>
            {/* Section title with export buttons */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="font-heading font-bold text-xl text-foreground">Analytique</h2>
                    <p className="text-sm text-muted-foreground">Indicateurs de productivite et performance</p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleExportPdf}
                        disabled={exportingPdf}
                        data-testid="export-pdf-btn"
                    >
                        {exportingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                        PDF
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleExportExcel}
                        disabled={exportingExcel}
                        data-testid="export-excel-btn"
                    >
                        {exportingExcel ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
                        Excel
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePreviewReport}
                        data-testid="preview-report-btn"
                        className="hidden sm:flex"
                    >
                        <Mail className="w-4 h-4 mr-2" />
                        Rapport email
                    </Button>
                    <Button
                        size="sm"
                        onClick={handleAiSummary}
                        disabled={loadingAi}
                        data-testid="ai-summary-btn"
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700"
                    >
                        {loadingAi ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                        Resume IA
                    </Button>
                </div>
            </div>

            {/* AI Recommendations Card */}
            {aiRecommendations.length > 0 && (
                <Card data-testid="ai-recommendations-card" className="border-blue-200 dark:border-blue-800 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                            <CardTitle className="font-heading text-base flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-blue-600" />
                                Recommandations IA de la semaine
                            </CardTitle>
                            <Button variant="ghost" size="sm" onClick={() => setAiRecommendations([])}>Fermer</Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {aiRecommendations.map((rec, i) => (
                                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-white/60 dark:bg-card/60 border border-blue-100 dark:border-blue-900">
                                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">{i + 1}</div>
                                    <p className="text-sm text-foreground leading-relaxed">{rec}</p>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Weekly Report Preview Modal */}
            {showPreview && reportPreview && (
                <Card data-testid="report-preview-card">
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                            <CardTitle className="font-heading text-base flex items-center gap-2">
                                <Mail className="w-4 h-4 text-blue-600" />
                                Apercu du rapport hebdomadaire
                            </CardTitle>
                            <div className="flex gap-2">
                                <Button
                                    size="sm"
                                    onClick={handleSendWeeklyReport}
                                    disabled={sendingReport}
                                    data-testid="send-report-btn"
                                    className={user?.role !== "admin" && user?.role !== "tenant_admin" ? "hidden" : ""}
                                >
                                    {sendingReport ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                                    Envoyer aux admins
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setShowPreview(false)}
                                >
                                    Fermer
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                            <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg text-center">
                                <div className="text-lg font-bold text-blue-700">{reportPreview.data?.active_projects}</div>
                                <div className="text-xs text-muted-foreground">Projets actifs</div>
                            </div>
                            <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg text-center">
                                <div className="text-lg font-bold text-green-700">{reportPreview.data?.completed_tasks_week}</div>
                                <div className="text-xs text-muted-foreground">Taches terminees</div>
                            </div>
                            <div className="p-3 bg-orange-50 dark:bg-orange-950/30 rounded-lg text-center">
                                <div className="text-lg font-bold text-orange-700">{reportPreview.data?.new_quotes}</div>
                                <div className="text-xs text-muted-foreground">Nouveaux devis</div>
                            </div>
                            <div className="p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg text-center">
                                <div className="text-lg font-bold text-purple-700">{reportPreview.data?.new_clients}</div>
                                <div className="text-xs text-muted-foreground">Nouveaux clients</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="flex justify-between p-2 bg-secondary/50 rounded">
                                <span className="text-muted-foreground">Facture cette semaine</span>
                                <span className="font-bold">{reportPreview.data?.invoiced_amount?.toFixed(0)} {reportPreview.data?.currency}</span>
                            </div>
                            <div className="flex justify-between p-2 bg-secondary/50 rounded">
                                <span className="text-muted-foreground">Encaisse</span>
                                <span className="font-bold text-green-600">{reportPreview.data?.paid_amount?.toFixed(0)} {reportPreview.data?.currency}</span>
                            </div>
                        </div>
                        {reportPreview.data?.top_performers?.length > 0 && (
                            <div className="mt-3">
                                <p className="text-xs text-muted-foreground mb-2">Top contributeurs :</p>
                                <div className="flex gap-2">
                                    {reportPreview.data.top_performers.map((p, i) => (
                                        <div key={i} className="px-3 py-1.5 bg-secondary/50 rounded-full text-xs">
                                            <span className="font-medium">{p.name}</span> <span className="text-muted-foreground">({p.tasks})</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Conversion KPIs */}
            {quoteConv && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="conversion-kpis">
                    <StatCard
                        icon={ArrowRightLeft}
                        label="Taux de conversion"
                        value={`${quoteConv.conversion_rate}%`}
                        sub={`${quoteConv.accepted}/${quoteConv.total_quotes} devis`}
                        color="bg-blue-600"
                    />
                    <StatCard
                        icon={TrendingUp}
                        label="Taux de paiement"
                        value={`${quoteConv.payment_rate}%`}
                        sub={`${quoteConv.paid_invoices}/${quoteConv.total_invoices} factures`}
                        color="bg-green-600"
                    />
                    <StatCard
                        icon={FolderKanban}
                        label="Devis en attente"
                        value={quoteConv.pending}
                        sub={`${quoteConv.rejected} rejete(s)`}
                        color="bg-orange-500"
                    />
                    <StatCard
                        icon={Clock}
                        label="Heures totales"
                        value={`${totalHours.toFixed(1)}h`}
                        sub={`${timeDist.length} projets`}
                        color="bg-purple-600"
                    />
                </div>
            )}

            {/* Row 1: Productivity + Team Performance */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Productivity by Project */}
                <Card data-testid="productivity-chart">
                    <CardHeader className="pb-2">
                        <CardTitle className="font-heading text-base flex items-center gap-2">
                            <FolderKanban className="w-4 h-4 text-blue-600" />
                            Productivite par projet
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {productivity.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-12">Aucune donnee disponible</p>
                        ) : (
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={productivity} layout="vertical" margin={{ left: 10 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                        <XAxis type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                                        <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                                        <Tooltip contentStyle={ChartTooltipStyle} />
                                        <Legend wrapperStyle={{ fontSize: 11 }} />
                                        <Bar dataKey="completed" name="Terminees" fill="#10B981" stackId="a" radius={[0, 0, 0, 0]} />
                                        <Bar dataKey="in_progress" name="En cours" fill="#F97316" stackId="a" />
                                        <Bar dataKey="todo" name="A faire" fill="#94A3B8" stackId="a" radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Team Performance */}
                <Card data-testid="team-performance-chart">
                    <CardHeader className="pb-2">
                        <CardTitle className="font-heading text-base flex items-center gap-2">
                            <Users className="w-4 h-4 text-green-600" />
                            Performance equipe
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {teamPerf.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-12">Aucune donnee disponible</p>
                        ) : (
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={teamPerf}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                        <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                                        <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                                        <Tooltip contentStyle={ChartTooltipStyle} formatter={(v, n) => [v, n === "assigned" ? "Assignees" : "Terminees"]} />
                                        <Legend wrapperStyle={{ fontSize: 11 }} />
                                        <Bar dataKey="assigned" name="Assignees" fill="#2563EB" radius={[4, 4, 0, 0]} />
                                        <Bar dataKey="completed" name="Terminees" fill="#10B981" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Row 2: Monthly Revenue + Time Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Monthly Revenue Line Chart */}
                <Card className="lg:col-span-8" data-testid="monthly-revenue-chart">
                    <CardHeader className="pb-2">
                        <CardTitle className="font-heading text-base flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-primary" />
                            Revenus mensuels (12 mois)
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-72">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={monthlyRev}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                                    <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                                    <Tooltip contentStyle={ChartTooltipStyle} formatter={(v) => [formatCurrency(v)]} />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                    <Line type="monotone" dataKey="paid" name="Paye" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
                                    <Line type="monotone" dataKey="total" name="Facture" stroke="#2563EB" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Time Distribution Pie Chart */}
                <Card className="lg:col-span-4" data-testid="time-distribution-chart">
                    <CardHeader className="pb-2">
                        <CardTitle className="font-heading text-base flex items-center gap-2">
                            <Clock className="w-4 h-4 text-purple-600" />
                            Repartition du temps
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {timeDist.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-12">Aucune entree de temps</p>
                        ) : (
                            <>
                                <div className="h-48">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={timeDist}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={45}
                                                outerRadius={70}
                                                paddingAngle={3}
                                                dataKey="hours"
                                            >
                                                {timeDist.map((_, i) => (
                                                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip contentStyle={ChartTooltipStyle} formatter={(v) => [`${v}h`]} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="space-y-1.5 mt-2">
                                    {timeDist.map((item, i) => (
                                        <div key={i} className="flex items-center justify-between text-xs">
                                            <div className="flex items-center gap-1.5">
                                                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                                                <span className="text-muted-foreground truncate max-w-[120px]">{item.name}</span>
                                            </div>
                                            <span className="font-mono font-medium">{item.hours}h</span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Row 3: Quote Conversion Trend */}
            {quoteConv && quoteConv.trend && quoteConv.trend.length > 0 && (
                <Card data-testid="conversion-trend-chart">
                    <CardHeader className="pb-2">
                        <CardTitle className="font-heading text-base flex items-center gap-2">
                            <ArrowRightLeft className="w-4 h-4 text-blue-600" />
                            Tendance conversion devis (6 mois)
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-56">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={quoteConv.trend}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                                    <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                                    <Tooltip contentStyle={ChartTooltipStyle} />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                    <Bar dataKey="quotes" name="Devis envoyes" fill="#94A3B8" radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="accepted" name="Devis acceptes" fill="#2563EB" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};
